// 契約一覧。ロールで見え方が変わる：
//  - staff/manager: 自部門の一覧（軽量列）
//  - secretariat/gm: 締結中フロービュー（全社・進行階段/分類/本社確認の列＋フィルタ。§7-3）
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, SelectInput, Spinner } from "../../components/ui";
import { StatusBadge } from "./StatusBadge";
import { ClassificationBadge, HqStatusBadge } from "./badges";
import { ContractDetailModal } from "./ContractDetailModal";
import { ContractLedgerView } from "./ContractLedgerView";
import {
  APPROVAL_STATUS,
  CONTRACT_TYPES,
  STAGE_LABELS,
  labelOf,
  type Option,
} from "../../masters";
import { formatAmount } from "../../lib/format";
import { api, ApiError } from "../../lib/api";
import { useAuth } from "../../auth";
import { t } from "../../i18n/zh-TW";
import type { Contract } from "../../lib/types";

const STATUS_FILTER_OPTIONS: Option[] = [
  { code: "all", label: t.flow.all },
  { code: "draft", label: APPROVAL_STATUS.draft.label },
  { code: "pending_team", label: APPROVAL_STATUS.pending_team.label },
  { code: "pending_div", label: APPROVAL_STATUS.pending_div.label },
  { code: "rejected", label: APPROVAL_STATUS.rejected.label },
  { code: "approved", label: APPROVAL_STATUS.approved.label },
];

const CLASS_FILTER_OPTIONS: Option[] = [
  { code: "all", label: t.flow.all },
  { code: "A", label: "A類" },
  { code: "B", label: "B類" },
  { code: "C", label: "C類" },
  { code: "unclassified", label: t.flow.unclassified },
];

export function ContractListPage() {
  const { user } = useAuth();
  const isManagement = user?.role === "secretariat" || user?.role === "gm";

  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");
  // 経営側の2ビュー切替（締結中フロー / 契約台帳。§7-3）。
  const [viewMode, setViewMode] = useState<"flow" | "ledger">("flow");

  function load() {
    setLoading(true);
    setError(null);
    api
      .listContracts()
      .then((res) => setContracts(res.contracts))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : t.errors.loadFailed))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  const filtered = useMemo(() => {
    return contracts.filter((c) => {
      if (statusFilter !== "all" && c.approval_status !== statusFilter) return false;
      if (classFilter === "unclassified" && c.classification) return false;
      if (classFilter !== "all" && classFilter !== "unclassified" && c.classification !== classFilter)
        return false;
      return true;
    });
  }, [contracts, statusFilter, classFilter]);

  const selected = contracts.find((c) => c.id === selectedId) ?? null;

  function onChanged(updated: Contract) {
    setContracts((list) => list.map((c) => (c.id === updated.id ? updated : c)));
  }

  return (
    <div>
      <div className="mb-5 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-800">
          {isManagement ? (viewMode === "ledger" ? t.ledger.title : t.flow.title) : t.list.title}
        </h1>
        <div className="flex items-center gap-2">
          {isManagement && (
            <div className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5 text-sm">
              <button
                onClick={() => setViewMode("flow")}
                className={`rounded-md px-3 py-1 ${
                  viewMode === "flow" ? "bg-brand-600 text-white" : "text-slate-600"
                }`}
              >
                {t.ledger.toggleFlow}
              </button>
              <button
                onClick={() => setViewMode("ledger")}
                className={`rounded-md px-3 py-1 ${
                  viewMode === "ledger" ? "bg-brand-600 text-white" : "text-slate-600"
                }`}
              >
                {t.ledger.toggleLedger}
              </button>
            </div>
          )}
          {user?.role === "staff" && (
            <Link to="/new">
              <Button>{t.list.newButton}</Button>
            </Link>
          )}
        </div>
      </div>

      {isManagement && viewMode === "flow" && (
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="mb-1 block text-xs text-slate-500">{t.flow.filterStatus}</span>
            <SelectInput
              options={STATUS_FILTER_OPTIONS}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-slate-500">{t.flow.filterClassification}</span>
            <SelectInput
              options={CLASS_FILTER_OPTIONS}
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
            />
          </label>
        </div>
      )}

      {loading ? (
        <div className="py-16">
          <Spinner label={t.common.loading} />
        </div>
      ) : error ? (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : isManagement && viewMode === "ledger" ? (
        <ContractLedgerView contracts={contracts} onOpen={(id) => setSelectedId(id)} />
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-400">
          {t.list.empty}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">{t.list.columnName}</th>
                {isManagement && <th className="px-4 py-3">{t.list.columnDept}</th>}
                <th className="px-4 py-3">{t.list.columnCounterparty}</th>
                {!isManagement && <th className="px-4 py-3">{t.list.columnType}</th>}
                <th className="px-4 py-3 text-right">{t.list.columnAmount}</th>
                {!isManagement && <th className="px-4 py-3">{t.list.columnDept}</th>}
                {isManagement ? (
                  <>
                    <th className="px-4 py-3">{t.flow.columnStage}</th>
                    <th className="px-4 py-3">{t.flow.columnClassification}</th>
                    <th className="px-4 py-3">{t.flow.columnHq}</th>
                  </>
                ) : (
                  <th className="px-4 py-3">{t.list.columnStatus}</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  className="cursor-pointer hover:bg-brand-50/40"
                >
                  <td className="px-4 py-3 text-sm font-medium text-slate-800">{c.contract_name}</td>
                  {isManagement && (
                    <td className="px-4 py-3 text-sm text-slate-600">{c.department}</td>
                  )}
                  <td className="px-4 py-3 text-sm text-slate-600">{c.counterparty}</td>
                  {!isManagement && (
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {labelOf(CONTRACT_TYPES, c.contract_type)}
                    </td>
                  )}
                  <td className="px-4 py-3 text-right text-sm text-slate-800">
                    {formatAmount(c.amount, c.currency)}
                  </td>
                  {!isManagement && (
                    <td className="px-4 py-3 text-sm text-slate-600">{c.department}</td>
                  )}
                  {isManagement ? (
                    <>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {STAGE_LABELS[c.stage] ?? c.stage}
                      </td>
                      <td className="px-4 py-3">
                        <ClassificationBadge value={c.classification} />
                      </td>
                      <td className="px-4 py-3">
                        <HqStatusBadge value={c.hq_review_status} />
                      </td>
                    </>
                  ) : (
                    <td className="px-4 py-3">
                      <StatusBadge status={c.approval_status} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && user && (
        <ContractDetailModal
          contract={selected}
          user={user}
          onClose={() => setSelectedId(null)}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}
